/* eslint-disable @typescript-eslint/no-use-before-define */
/************************************
    Variables
************************************/

const VERSION = '3.0.0';
const _formats: { [key: string]: any } = {};
const _locales: { [key: string]: any } = {};

export interface NumeralOptions {
	currentLocale: string;
	zeroFormat: string | null;
	nullFormat: string | null;
	defaultFormat: string;
	scalePercentBy100: boolean;
}

const defaults: NumeralOptions = {
	currentLocale: 'en',
	zeroFormat: null,
	nullFormat: null,
	defaultFormat: '0,0',
	scalePercentBy100: true
};

const _options: NumeralOptions = Object.assign({}, defaults);

/************************************
    Constructors
************************************/

export type NumeralInput = string | number | Numeral | undefined | null;
export type NumeralValue = number | Numeral;

// Numeral prototype object
export class Numeral {
	private _input: NumeralInput;
	private _value: number | null;

	static version = VERSION;

	constructor(input: NumeralInput, value: number | null) {
		this._input = input;
		this._value = value;
	}

	/**
	 * Process an input value and create a Numeral object.
	 *
	 * @static
	 * @param {NumeralInput} input
	 * @returns {Numeral}
	 * @memberof Numeral
	 */
	static numeral(input: NumeralInput): Numeral {
		let value: number | null;
		const { options, formats } = numeral;

		if (Numeral.isNumeral(input)) {
			value = input.value();
		} else if (input === 0 || typeof input === 'undefined') {
			value = 0;
		} else if (input === null || _.isNaN(input)) {
			value = null;
		} else if (typeof input === 'string') {
			if (options.zeroFormat && input === options.zeroFormat) {
				value = 0;
			} else if ((options.nullFormat && input === options.nullFormat) || !input.replace(/[^0-9]+/g, '').length) {
				value = null;
			} else {
				let unformatFunction;

				for (const kind in formats) {
					const regexp =
						typeof formats[kind].regexps.unformat === 'function'
							? formats[kind].regexps.unformat()
							: formats[kind].regexps.unformat;

					if (regexp && input.match(regexp)) {
						unformatFunction = formats[kind].unformat;
						break;
					}
				}

				unformatFunction = unformatFunction || _.stringToNumber;
				value = unformatFunction(input);
			}
		} else {
			value = Number(input) || null;
		}

		return new Numeral(input, value);
	}

	clone() {
		return numeral(this);
	}

	format(inputString: string, roundingFunction?: (x: number) => number) {
		const value = this._value;
		const { options, formats } = numeral;
		const format = inputString || options.defaultFormat;
		let kind;
		let output;
		let formatFunction;

		// make sure we have a roundingFunction
		roundingFunction = roundingFunction || Math.round;

		// format based on value
		if (value === 0 && options.zeroFormat !== null) {
			output = options.zeroFormat;
		} else if (value === null && options.nullFormat !== null) {
			output = options.nullFormat;
		} else {
			for (kind in formats) {
				if (format.match(formats[kind].regexps.format)) {
					formatFunction = formats[kind].format;
					break;
				}
			}

			formatFunction = formatFunction || _.numberToFormat;
			output = formatFunction(value, format, roundingFunction);
		}

		return output;
	}

	value() {
		return this._value;
	}

	input() {
		return this._input;
	}

	set(value: NumeralValue) {
		this._value = numeral(value).value();

		return this;
	}

	add(v: NumeralValue) {
		const value = numeral(v).value();
		const { _value } = this;
		const corrFactor = _.correctionFactor.call(null, _value!, value!);

		const cback = (accum: number, curr: number) => {
			return accum + Math.round(corrFactor * curr);
		};

		this._value = _.reduce([_value, value], cback, 0) / corrFactor;

		return this;
	}

	subtract(v: NumeralValue) {
		const value = numeral(v).value();
		const corrFactor = _.correctionFactor.call(null, this._value!, value!);

		function cback(accum: number, curr: number) {
			return accum - Math.round(corrFactor * curr);
		}

		this._value = _.reduce([value], cback, Math.round(this._value! * corrFactor)) / corrFactor;

		return this;
	}

	multiply(value: NumeralValue) {
		function cback(accum: number, curr: number) {
			const corrFactor = _.correctionFactor(accum, curr);
			return (
				(Math.round(accum * corrFactor) * Math.round(curr * corrFactor)) / Math.round(corrFactor * corrFactor)
			);
		}

		this._value = _.reduce([this._value, numeral(value).value()], cback, 1);

		return this;
	}

	divide(value: NumeralValue) {
		function cback(accum: number, curr: number) {
			const corrFactor = _.correctionFactor(accum, curr);
			return Math.round(accum * corrFactor) / Math.round(curr * corrFactor);
		}

		this._value = _.reduce([this._value, numeral(value).value()], cback);

		return this;
	}

	difference(value: NumeralValue) {
		return Math.abs(
			numeral(this._value)
				.subtract(value)
				.value() as number
		);
	}

	static isNumeral(obj: any): obj is Numeral {
		return obj instanceof Numeral;
	}
}

/* ------------------------------------------------- */
// helper functions
const _ = {
	// formats numbers separators, decimals places, signs, abbreviations
	numberToFormat(value: number, format: string, roundingFunction: (x: number) => number) {
		const { locales } = numeral;
		const locale = locales[numeral.options.currentLocale];
		let negP = false;
		let optDec = false;
		let leadingCount = 0;
		let abbr = '';
		const trillion = 1000000000000;
		const billion = 1000000000;
		const million = 1000000;
		const thousand = 1000;
		let decimal = '';
		let neg = false;

		// force abbreviation
		let abbrForce: any;

		let signed: number = -1;
		let output;

		// make sure we never format a null value
		value = value || 0;

		const abs = Math.abs(value);

		// see if we should use parentheses for negative number or if we should prefix with a sign
		// if both are present we default to parentheses
		if (format.includes('(')) {
			negP = true;
			format = format.replace(/[(|)]/g, '');
		} else if (format.includes('+') || format.includes('-')) {
			signed = format.includes('+') ? format.indexOf('+') : value < 0 ? format.indexOf('-') : -1;
			format = format.replace(/[+|-]/g, '');
		}

		// see if abbreviation is wanted
		if (format.includes('a')) {
			abbrForce = format.match(/a(k|m|b|t)?/);

			abbrForce = abbrForce ? abbrForce[1] : false;

			// check for space before abbreviation
			if (format.includes(' a')) {
				abbr = ' ';
			}

			format = format.replace(new RegExp(`${abbr}a[kmbt]?`), '');

			if ((abs >= trillion && !abbrForce) || abbrForce === 't') {
				// trillion
				abbr += locale.abbreviations.trillion;
				value = value / trillion;
			} else if ((abs < trillion && abs >= billion && !abbrForce) || abbrForce === 'b') {
				// billion
				abbr += locale.abbreviations.billion;
				value = value / billion;
			} else if ((abs < billion && abs >= million && !abbrForce) || abbrForce === 'm') {
				// million
				abbr += locale.abbreviations.million;
				value = value / million;
			} else if ((abs < million && abs >= thousand && !abbrForce) || abbrForce === 'k') {
				// thousand
				abbr += locale.abbreviations.thousand;
				value = value / thousand;
			}
		}

		// check for optional decimals
		if (format.includes('[.]')) {
			optDec = true;
			format = format.replace('[.]', '.');
		}

		// break number and format
		let int = value.toString().split('.')[0];
		let precision: string | string[] = format.split('.')[1];
		const thousands: number = format.indexOf(',');
		leadingCount = (
			format
				.split('.')[0]
				.split(',')[0]
				.match(/0/g) || []
		).length;

		if (precision) {
			if (precision.includes('[')) {
				precision = precision.replace(']', '');
				precision = precision.split('[');
				decimal = _.toFixed(
					value,
					precision[0].length + precision[1].length,
					roundingFunction,
					precision[1].length
				);
			} else {
				decimal = _.toFixed(value, precision.length, roundingFunction);
			}

			int = decimal.split('.')[0];

			if (decimal.includes('.')) {
				decimal = locale.delimiters.decimal + decimal.split('.')[1];
			} else {
				decimal = '';
			}

			if (optDec && Number(decimal.slice(1)) === 0) {
				decimal = '';
			}
		} else {
			int = _.toFixed(value, 0, roundingFunction);
		}

		// check abbreviation again after rounding
		if (abbr && !abbrForce && Number(int) >= 1000 && abbr !== locale.abbreviations.trillion) {
			int = String(Number(int) / 1000);

			switch (abbr) {
				case locale.abbreviations.thousand:
					abbr = locale.abbreviations.million;
					break;
				case locale.abbreviations.million:
					abbr = locale.abbreviations.billion;
					break;
				case locale.abbreviations.billion:
					abbr = locale.abbreviations.trillion;
					break;
			}
		}

		// format number
		if (int.includes('-')) {
			int = int.slice(1);
			neg = true;
		}

		if (int.length < leadingCount) {
			for (let i = leadingCount - int.length; i > 0; i--) {
				int = `0${int}`;
			}
		}

		if (thousands > -1) {
			int = int.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${locale.delimiters.thousands}`);
		}

		if (format.indexOf('.') === 0) {
			int = '';
		}

		output = int + decimal + (abbr ? abbr : '');

		if (negP) {
			output = (negP && neg ? '(' : '') + output + (negP && neg ? ')' : '');
		} else {
			if (signed >= 0) {
				output = signed === 0 ? (neg ? '-' : '+') + output : output + (neg ? '-' : '+');
			} else if (neg) {
				output = `-${output}`;
			}
		}

		return output;
	},

	// unformats numbers separators, decimals places, signs, abbreviations
	stringToNumber(s: string) {
		const { locales, options } = numeral;
		const locale = locales[options.currentLocale];
		const stringOriginal = s;

		const abbreviations: { [key: string]: number } = {
			thousand: 3,
			million: 6,
			billion: 9,
			trillion: 12
		};

		let value: number | null;

		if (options.zeroFormat && s === options.zeroFormat) {
			value = 0;
		} else if ((options.nullFormat && s === options.nullFormat) || !s.replace(/[^0-9]+/g, '').length) {
			value = null;
		} else {
			value = 1;

			if (locale.delimiters.decimal !== '.') {
				s = s.replace(/\./g, '').replace(locale.delimiters.decimal, '.');
			}

			for (const abbreviation in abbreviations) {
				const regexp = new RegExp(
					`[^a-zA-Z]${locale.abbreviations[abbreviation]}(?:\\)|(\\${locale.currency.symbol})?(?:\\))?)?$`
				);

				if (stringOriginal.match(regexp)) {
					value *= Math.pow(10, abbreviations[abbreviation]);
					break;
				}
			}

			// check for negative number
			value *= (s.split('-').length + Math.min(s.split('(').length - 1, s.split(')').length - 1)) % 2 ? 1 : -1;

			// remove non numbers
			s = s.replace(/[^0-9.]+/g, '');

			value *= Number(s);
		}

		return value;
	},

	isNaN(value: any) {
		return typeof value === 'number' && isNaN(value);
	},

	insert(s: string, subString: string, start: number) {
		return s.slice(0, start) + subString + s.slice(start);
	},

	reduce(array: any[], callback: (...args: any[]) => any, initialValue?: any) {
		if (this === null) {
			throw new TypeError('Array.prototype.reduce called on null or undefined');
		}

		if (typeof callback !== 'function') {
			throw new TypeError(`${callback} is not a function`);
		}

		const t = Object(array);
		const len = t.length >>> 0;
		let k = 0;
		let value;

		if (initialValue) {
			value = initialValue;
		} else {
			while (k < len && !(k in t)) {
				k++;
			}

			if (k >= len) {
				throw new TypeError('Reduce of empty array with no initial value');
			}

			value = t[k++];
		}
		for (; k < len; k++) {
			if (k in t) {
				value = callback(value, t[k], k, t);
			}
		}
		return value;
	},

	/**
	 * Computes the multiplier necessary to make x >= 1,
	 * effectively eliminating miscalculations caused by
	 * finite precision.
	 */
	multiplier(x: number) {
		const parts = x.toString().split('.');

		return parts.length < 2 ? 1 : Math.pow(10, parts[1].length);
	},

	/**
	 * Given a variable number of arguments, returns the maximum
	 * multiplier that must be used to normalize an operation involving
	 * all of them.
	 */
	correctionFactor(...args: number[]) {
		return args.reduce((accum, next) => {
			const mn = _.multiplier(next);
			return accum > mn ? accum : mn;
		}, 1);
	},

	/**
	 * Implementation of toFixed() that treats floats more like decimals
	 *
	 * Fixes binary rounding issues (eg. (0.615).toFixed(2) === '0.61') that present
	 * problems for accounting- and finance-related software.
	 */
	toFixed(value: any, maxDecimals: number, roundingFunction: (x: number) => number, optionals: number = 0) {
		const splitValue = value.toString().split('.');
		const minDecimals = maxDecimals - optionals;

		// Use the smallest precision value possible to avoid errors from floating point representation
		const boundedPrecision =
			splitValue.length === 2 ? Math.min(Math.max(splitValue[1].length, minDecimals), maxDecimals) : minDecimals;

		const power = Math.pow(10, boundedPrecision);

		// Multiply up by precision, round accurately, then divide and use native toFixed():
		let output = (roundingFunction(+`${value}e+${boundedPrecision}`) / power).toFixed(boundedPrecision);

		if (optionals > maxDecimals - boundedPrecision) {
			const optionalsRegExp = new RegExp(`\\.?0{1,${optionals - (maxDecimals - boundedPrecision)}}$`);
			output = output.replace(optionalsRegExp, '');
		}

		return output;
	}
};

const helperFuncs = _;

/*
 * numeral object
 */
export function numeral(input: NumeralInput) {
	return Numeral.numeral(input);
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace numeral {
	export const isNumeral = Numeral.isNumeral;
	export const version = Numeral.version;
	// available options
	export const options = _options;
	// available formats
	export const formats = _formats;
	// available locales
	export const locales = _locales;

	export const _ = helperFuncs;

	// This function sets the current locale.  If
	// no arguments are passed in, it will simply return the current global
	// locale key.
	export const locale = (key?: string) => {
		if (key) {
			options.currentLocale = key.toLowerCase();
		}

		return options.currentLocale;
	};

	// This function provides access to the loaded locale data.  If
	// no arguments are passed in, it will simply return the current
	// global locale object.
	export const localeData = (key?: string) => {
		if (!key) {
			return locales[options.currentLocale];
		}

		key = key.toLowerCase();

		if (!locales[key]) {
			throw new Error(`Unknown locale : ${key}`);
		}

		return locales[key];
	};

	export const reset = () => {
		Object.assign(options, defaults);
	};

	export const zeroFormat = (format?: string) => {
		options.zeroFormat = typeof format === 'string' ? format : null;
	};

	export const nullFormat = (format?: string) => {
		options.nullFormat = typeof format === 'string' ? format : null;
	};

	export const defaultFormat = (format?: string) => {
		options.defaultFormat = typeof format === 'string' ? format : '0.0';
	};

	export const register = (type: string, name: string, format: string | object) => {
		name = name.toLowerCase();

		if ((numeral as any)[`${type}s`][name]) {
			throw new TypeError(`${name} ${type} already registered.`);
		}

		(numeral as any)[`${type}s`][name] = format;

		return format;
	};

	export const validate = (val: string, culture?: string) => {
		let _thousandSep: string;
		let _valArray: any[];
		let localeData;
		let temp;

		// trim whitespaces from either sides
		val = val.trim();

		// if val is just digits return true
		if (val.match(/^\d+$/)) {
			return true;
		}

		// if val is empty return false
		if (val === '') {
			return false;
		}

		// get the decimal and thousands separator from numeral.localeData
		try {
			// check if the culture is understood by numeral. if not, default it to current locale
			localeData = numeral.localeData(culture);
		} catch (e) {
			localeData = numeral.localeData(numeral.locale());
		}

		// setup the delimiters and currency symbol based on culture/locale
		const _currSymbol = localeData.currency.symbol;
		const _abbrObj = localeData.abbreviations;
		const _decimalSep = localeData.delimiters.decimal;
		if (localeData.delimiters.thousands === '.') {
			_thousandSep = '\\.';
		} else {
			_thousandSep = localeData.delimiters.thousands;
		}

		// validating currency symbol
		temp = val.match(/^[^\d]+/);
		if (temp !== null) {
			val = val.substr(1);
			if (temp[0] !== _currSymbol) {
				return false;
			}
		}

		// validating abbreviation symbol
		temp = val.match(/[^\d]+$/);
		if (temp !== null) {
			val = val.slice(0, -1);
			if (
				temp[0] !== _abbrObj.thousand &&
				temp[0] !== _abbrObj.million &&
				temp[0] !== _abbrObj.billion &&
				temp[0] !== _abbrObj.trillion
			) {
				return false;
			}
		}

		const _thousandRegEx = new RegExp(`${_thousandSep}{2}`);

		if (!val.match(/[^\d.,]/g)) {
			_valArray = val.split(_decimalSep);
			if (_valArray.length > 2) {
				return false;
			} else {
				if (_valArray.length < 2) {
					return !!_valArray[0].match(/^\d+.*\d$/) && !_valArray[0].match(_thousandRegEx);
				} else {
					if (_valArray[0].length === 1) {
						return (
							!!_valArray[0].match(/^\d+$/) &&
							!_valArray[0].match(_thousandRegEx) &&
							!!_valArray[1].match(/^\d+$/)
						);
					} else {
						return (
							!!_valArray[0].match(/^\d+.*\d$/) &&
							!_valArray[0].match(_thousandRegEx) &&
							!!_valArray[1].match(/^\d+$/)
						);
					}
				}
			}
		}

		return false;
	};
}

/************************************
    Default Locale && Format
************************************/

numeral.register('locale', 'en', {
	delimiters: {
		thousands: ',',
		decimal: '.'
	},
	abbreviations: {
		thousand: 'k',
		million: 'm',
		billion: 'b',
		trillion: 't'
	},
	ordinal(val: number) {
		const b = val % 10;
		return ~~((val % 100) / 10) === 1 ? 'th' : b === 1 ? 'st' : b === 2 ? 'nd' : b === 3 ? 'rd' : 'th';
	},
	currency: {
		symbol: '$'
	}
});
